import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";

import { ErrorBanner } from "../components/ErrorBanner";
import { Skeleton } from "../components/Skeleton";
import { formatTrayFraction } from "../lib/api";
import { useCycles, useEventsByRange } from "../lib/queries";
import {
  addDays,
  addMonths,
  firstOfMonth,
  formatMonthHeader,
  monthGrid,
  monthOf,
  todayISO,
  type IsoDate,
} from "../lib/date";
import { fonts, kindStyles, radius, shadow, tokens, trayColors } from "../styles/theme";

const WEEKDAY = ["S", "M", "T", "W", "T", "F", "S"];

export function CalendarView() {
  const [anchor, setAnchor] = useState<IsoDate>(firstOfMonth(todayISO()));
  const grid = useMemo(() => monthGrid(anchor), [anchor]);
  const rangeFrom = grid[0] ?? anchor;
  const rangeTo = grid[grid.length - 1] ?? anchor;
  const events = useEventsByRange(rangeFrom, rangeTo);
  // Need the full cycles list (not just active) so a harvest event on a
  // since-completed cycle still picks up its fraction label.
  const cycles = useCycles();
  const [selected, setSelected] = useState<IsoDate | null>(null);

  const fractionByCycle = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of cycles.data ?? []) m.set(c.id, c.tray_fraction ?? 1);
    return m;
  }, [cycles.data]);

  const byDate = useMemo(() => {
    const m = new Map<IsoDate, typeof events.data>();
    for (const e of events.data ?? []) {
      const list = m.get(e.date) ?? [];
      list.push(e);
      m.set(e.date, list);
    }
    return m;
  }, [events.data]);

  const selectedEvents = selected ? (byDate.get(selected) ?? []) : [];

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => setAnchor((a) => addMonths(a, -1))}
          style={iconBtn}
        >
          <ChevronLeft size={18} />
        </button>
        <div
          style={{
            fontSize: "24px",
            color: tokens.ink,
          }}
        >
          {formatMonthHeader(anchor)}
        </div>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => setAnchor((a) => addMonths(a, 1))}
          style={iconBtn}
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {events.isLoading && <Skeleton count={6} height={60} />}
      {events.isError && (
        <ErrorBanner message={`Failed to load events: ${(events.error as Error).message}`} />
      )}

      <div
        role="grid"
        aria-label={`Calendar for ${formatMonthHeader(anchor)}`}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "1px",
          background: tokens.line,
          border: `1px solid ${tokens.line}`,
          borderRadius: radius.md,
          overflow: "hidden",
          boxShadow: shadow.xs,
        }}
      >
        {WEEKDAY.map((d, i) => (
          <div
            key={i}
            role="columnheader"
            style={{
              padding: "8px 4px",
              background: tokens.sunken,
              textAlign: "center",
              fontSize: "10px",
              letterSpacing: "0.18em",
              color: tokens.muted,
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            {d}
          </div>
        ))}
        {grid.map((date) => {
          const inMonth = monthOf(date) === monthOf(anchor);
          const dayEvents = byDate.get(date) ?? [];
          const trays = Array.from(new Set(dayEvents.map((e) => e.tray_id).filter(Boolean)));
          const active = date === selected;
          return (
            <button
              key={date}
              type="button"
              role="gridcell"
              onClick={() => setSelected(date)}
              aria-label={`${date}, ${dayEvents.length} events`}
              style={{
                minHeight: "64px",
                padding: "6px 6px 4px",
                background: active ? tokens.sageTint : tokens.card,
                border: "none",
                borderBottom: active ? `2px solid ${tokens.sage}` : "2px solid transparent",
                color: inMonth ? tokens.ink : tokens.faint,
                opacity: inMonth ? 1 : 0.55,
                cursor: "pointer",
                fontFamily: "inherit",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                alignItems: "stretch",
                transition: "background 0.15s ease",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  textAlign: "right",
                  fontWeight: active ? 600 : 500,
                  fontFamily: fonts.mono,
                }}
              >
                {Number(date.slice(-2))}
              </div>
              <div style={{ display: "flex", gap: "3px", flexWrap: "wrap" }}>
                {trays.map((t) => (
                  <span
                    key={t as string}
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: radius.pill,
                      background: trayColors[t as string] ?? tokens.faint,
                      display: "inline-block",
                    }}
                  />
                ))}
              </div>
              {dayEvents.length > 0 && (
                <div
                  style={{
                    marginTop: "auto",
                    fontSize: "10px",
                    color: tokens.muted,
                    fontFamily: fonts.mono,
                  }}
                >
                  {dayEvents.length}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {selected && (
        <div
          role="region"
          aria-label={`Events for ${selected}`}
          style={{
            marginTop: "20px",
            padding: "18px",
            background: tokens.card,
            border: `1px solid ${tokens.line}`,
            borderRadius: radius.md,
            boxShadow: shadow.xs,
          }}
        >
          <div
            style={{
              fontSize: "18px",
              color: tokens.ink,
              marginBottom: "12px",
            }}
          >
            {selected}
          </div>
          {selectedEvents.length === 0 && (
            <div style={{ fontSize: "13px", color: tokens.muted }}>No events.</div>
          )}
          {selectedEvents.map((ev) => {
            const style = kindStyles[ev.kind];
            const trayColor = ev.tray_id ? (trayColors[ev.tray_id] ?? tokens.faint) : tokens.faint;
            return (
              <div
                key={ev.id}
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  padding: "10px 12px",
                  marginBottom: "6px",
                  borderLeft: `3px solid ${trayColor}`,
                  background: tokens.sunken,
                  borderRadius: radius.sm,
                  fontSize: "13px",
                }}
              >
                <span
                  style={{
                    fontSize: "11px",
                    padding: "2px 8px",
                    background: style.bg,
                    color: style.fg,
                    borderRadius: radius.pill,
                    fontWeight: 600,
                    letterSpacing: "0.03em",
                    whiteSpace: "nowrap",
                  }}
                >
                  {style.label.charAt(0) + style.label.slice(1).toLowerCase()}
                </span>
                {ev.tray_id && (
                  <span
                    style={{
                      color: tokens.muted,
                      fontWeight: 600,
                      fontSize: "11px",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {ev.tray_id}
                  </span>
                )}
                <span
                  style={{
                    flex: 1,
                    color: tokens.inkSoft,
                    textDecoration: ev.completed_at ? "line-through" : "none",
                  }}
                >
                  {ev.text}
                  {(() => {
                    const f = ev.cycle_id ? fractionByCycle.get(ev.cycle_id) : undefined;
                    return f !== undefined && f < 0.999 ? (
                      <span style={{ color: tokens.muted, marginLeft: "6px" }}>
                        — {formatTrayFraction(f)} tray
                      </span>
                    ) : null;
                  })()}
                </span>
              </div>
            );
          })}
          <div style={{ marginTop: "12px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setSelected((d) => (d ? addDays(d, -1) : d))}
              style={ghostBtn}
            >
              Prev day
            </button>
            <button
              type="button"
              onClick={() => setSelected((d) => (d ? addDays(d, 1) : d))}
              style={ghostBtn}
            >
              Next day
            </button>
            <button type="button" onClick={() => setSelected(null)} style={ghostBtn}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const iconBtn = {
  background: "transparent",
  border: `1px solid ${tokens.line}`,
  color: tokens.muted,
  width: "44px",
  height: "44px",
  borderRadius: radius.sm,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "inherit",
  transition: "background 0.15s ease",
} as const;

const ghostBtn = {
  background: tokens.card,
  border: `1px solid ${tokens.lineStrong}`,
  color: tokens.inkSoft,
  padding: "8px 14px",
  minHeight: "40px",
  fontFamily: "inherit",
  fontSize: "13px",
  fontWeight: 500,
  cursor: "pointer",
  borderRadius: radius.sm,
} as const;
